import { AlertTriangle, Info, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Chalk } from '../../lib/chalk';
import { CHALK } from '../../lib/chalk';
import { cn } from '../../lib/cn';
import { Button } from './Button';
import { ChalkTitle } from './Chalk';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-raised', className)} aria-hidden />;
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  color = 'blue',
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  color?: Chalk;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className={cn('mb-4 rounded-full border-2 border-dashed p-4', CHALK[color].border, CHALK[color].text)}>{icon}</div>
      )}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  error,
  onRetry,
  className,
}: {
  title?: string;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown error');
  return (
    <div role="alert" className={cn('flex flex-col items-center px-6 py-10 text-center', className)}>
      <div className="mb-3 rounded-full border-2 border-dashed border-danger/40 p-3 text-danger">
        <AlertTriangle className="h-5 w-5" aria-hidden />
      </div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 max-w-md text-sm text-muted">{message}</p>
      {onRetry && (
        <Button className="mt-4" size="sm" onClick={onRetry} icon={<RefreshCw className="h-3.5 w-3.5" />}>
          Try again
        </Button>
      )}
    </div>
  );
}

type Tone = 'info' | 'warn' | 'danger' | 'ok';
const TONE: Record<Tone, string> = {
  info: 'border-chalk-blue/35 bg-chalk-blue/10 text-ink',
  warn: 'border-chalk-yellow/35 bg-chalk-yellow/10 text-ink',
  danger: 'border-danger/40 bg-danger/10 text-ink',
  ok: 'border-ok/35 bg-ok/10 text-ink',
};
const TONE_ICON: Record<Tone, string> = { info: 'text-chalk-blue', warn: 'text-chalk-yellow', danger: 'text-danger', ok: 'text-ok' };

export function Notice({
  tone = 'info',
  title,
  children,
  className,
  action,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <div className={cn('flex items-start gap-3 rounded-lg border border-l-4 px-4 py-3 text-sm', TONE[tone], className)}>
      {tone === 'info' ? (
        <Info className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_ICON[tone])} aria-hidden />
      ) : (
        <AlertTriangle className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_ICON[tone])} aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn('text-muted', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  color = 'yellow',
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  color?: Chalk;
}) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <ChalkTitle color={color}>{title}</ChalkTitle>
        {description && <p className="mt-2 max-w-2xl text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
